/**
 * /pricing — three-tier plan comparison + billing FAQs.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/chrome/PageHeader';
import { usePageMeta } from '../../hooks/usePageMeta';

// Marketing names (Trial / Solo / Pro) differ from the backend tier enum
// (free / essentials / pro). When per-tier Stripe links land in Day 8,
// the ctaHref function will need to map: trial→free, solo→essentials, pro→pro.
// Until then ctaHref ignores _id entirely (single shared link or mailto fallback).
interface Tier {
  id: 'trial' | 'solo' | 'pro';
  /** Backend tier slug for Stripe checkout routing. */
  backendTier: 'free' | 'essentials' | 'pro';
  name: string;
  price: string;
  cadence: string;
  blurb: string;
  features: string[];
  cta: string;
  highlight?: boolean;
}

const TIERS: Tier[] = [
  {
    id: 'trial',
    backendTier: 'free',
    name: 'Trial',
    price: 'Free',
    cadence: '7 days',
    blurb: 'Full access when we go live. No card required.',
    features: ['All features unlocked at launch', 'Real-time data', '5 indices', 'Cancel anytime'],
    cta: 'Create free account',
  },
  {
    id: 'solo',
    backendTier: 'essentials',
    name: 'Essentials',
    price: '$49.99',
    cadence: 'per month',
    blurb: 'Index gamma at a glance — SPY, SPX, QQQ.',
    features: ['SPY, SPX, QQQ heatmaps', '30 s data refresh', 'Ambient ticker bar', 'Email support'],
    cta: 'Choose Essentials',
    highlight: true,
  },
  {
    id: 'pro',
    backendTier: 'pro',
    name: 'Pro',
    price: '$99.99',
    cadence: 'per month',
    blurb: 'Every ticker, full toolkit, 5-day replay.',
    features: [
      'Every Tradier-listed ticker',
      '5-day historical replay',
      'Dashboard, Risk, Scanner, Simulations',
      'Discord alert webhooks (coming soon)',
      'Priority support',
    ],
    cta: 'Choose Pro',
  },
];

const BILLING_FAQ = [
  { q: 'When am I billed?', a: 'Day 8 — the day after your 7-day free trial ends. We bill in USD via Stripe.' },
  { q: 'Can I cancel anytime?', a: 'Yes. Cancel from your account settings or by replying to any GammaVision email. No questions asked.' },
  { q: 'Do you offer refunds?', a: 'If you cancel within 14 days of your first paid charge, we refund in full.' },
  { q: 'What happens to my data after I cancel?', a: 'Your account settings are kept for 90 days in case you come back. Touch counts and saved layouts are wiped immediately on request.' },
];

const Pricing: React.FC = () => {
  usePageMeta(
    'Pricing — GammaVision',
    'Plans from $49.99/mo. 7-day free trial. Cancel anytime.',
  );
  const stripeLink = import.meta.env.VITE_STRIPE_PAYMENT_LINK;
  const validStripe = stripeLink && !/REPLACE_ME/i.test(stripeLink);
  // Single payment link for now — tier selection happens inside the Stripe checkout.
  // When per-tier links land, swap to: /api/stripe/checkout/${tier.backendTier}
  // which routes trial→free, solo→essentials, pro→pro on the backend.
  const ctaHref = (_id: Tier['id']) => validStripe ? stripeLink! : 'mailto:hello@gammavision.app?subject=GammaVision%20paid%20plan%20signup';

  return (
    <>
      <PageHeader title="PRICING" subtitle="One platform, two paid tiers, no surprise fees." />

      <div className="gv-options-body">
        {!validStripe && (
          <div className="gv-disclaimer">
            <strong>Public checkout opens at GA.</strong> Until then, paid-tier CTAs open an
            email — we'll set you up with manual invoicing within one business day.
          </div>
        )}

        <section className="gv-tiers">
          {TIERS.map(t => (
            <article key={t.id} className={`gv-tier${t.highlight ? ' gv-tier--hi' : ''}`}>
              {t.highlight && <span className="gv-tier-badge">MOST POPULAR</span>}
              <h2 className="gv-tier-name">{t.name}</h2>
              <p className="gv-tier-price">
                <span className="gv-tier-amount">{t.price}</span>
                <span className="gv-tier-cadence">{t.cadence}</span>
              </p>
              <p className="gv-tier-blurb">{t.blurb}</p>
              <ul className="gv-tier-features">
                {t.features.map((f, i) => <li key={i}><span className="gv-tier-check">✓</span> {f}</li>)}
              </ul>
              {t.id === 'trial' ? (
                <Link to="/signup" className="gv-cta-primary gv-tier-cta">{t.cta} →</Link>
              ) : (
                // Raw <a> is correct here: href resolves to either an external Stripe
                // checkout URL (https://) or a mailto: — neither is an internal route,
                // so react-router <Link> would be wrong (it would treat them as paths).
                <a className="gv-cta-primary gv-tier-cta" href={ctaHref(t.id)} target={validStripe ? '_blank' : undefined} rel={validStripe ? 'noopener noreferrer' : undefined}>
                  {t.cta} →
                </a>
              )}
            </article>
          ))}
        </section>

        <section className="gv-card">
          <div className="gv-panel-title">Billing FAQs</div>
          <div className="gv-faq">
            {BILLING_FAQ.map((f, i) => (
              <details key={i}>
                <summary>{f.q}</summary>
                <div>{f.a}</div>
              </details>
            ))}
          </div>
        </section>
      </div>
    </>
  );
};

export default Pricing;
